// ACC-439: a learner's preregistered (queued) OD courses, projected into the shape the
// course card already renders.
//
// Under the ACC-427 deferred-enrollment design a preregistration is NOT an enrollment: no
// /api/enrollments row exists until the scheduled run fires. So these come from a second
// source (Aether's queued bulk_enrollment_runs, carried on api-hub's enrollments read as
// `scheduled`) and are projected here into enrollment-shaped objects, so both "My Upcoming Courses"
// views (Dashboard + My Courses) can render them through their existing courseProps
// mappings without a parallel card.
//
// Pure on purpose: the fetch and the store live in sessionInitializer, everything below is
// data in, data out, so it can be tested without mounting either view.

import CourseModel from '@/data_models/course.model';

// The card's Options menu, unenroll and CTA all key off these. A preregistration is
// read-only until it fires, so the card must get a label it greys out (see
// MyCourses.vue unavailableClass: 'Access Course' + a future start = 'unavailable')
// and no unenroll affordance.
const READ_ONLY_ENROLLMENT_FIELDS = {
  _buttonLabel: 'Access Course',
  _buttonAction: '',
  _unenroll: false,
  _enrollmentStatus: 'Active',
  _grade: 0,
  _currentGrade: '0',
  _finalGrade: '',
  _isPathwayCourse: false
};

// MM/DD/YYYY in Phoenix time, matching CourseModel.formatDate — the card compares the
// start date against `new Date()` and prints it with getDateString, so an ISO UTC
// timestamp would read as the previous day for any evening-UTC schedule.
export function toCourseDate(value) {
  if (!value) return '';

  const date = new Date(value);
  if (isNaN(date.getTime())) return '';

  const [month, day, year] = date
    .toLocaleDateString('en-US', {
      timeZone: 'America/Phoenix',
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    })
    .split('/');

  return `${month}/${day}/${year}`;
}

// Catalog course (CourseModel, from /api/degree-courses) -> the enrollment's _courseInfo
// keys the card reads. Only the descriptive fields: anything that describes *access*
// (course URLs, Canvas ids, LMS) is deliberately left off — there is no enrollment to
// access yet, and a populated URL would make the greyed CTA openable.
function courseInfoFromCatalog(course) {
  if (!course) return {};

  return {
    _courseName: course._courseName,
    _coursePrefix: course._coursePrefix,
    _coursePrefixDisplayLabel: course._coursePrefixDisplayLabel,
    _coursePrefixAliases: course._coursePrefixAliases,
    _courseDescription: course._courseDescription,
    _courseTooltip: course._courseTooltip,
    _asuCredits: course._asuCredits,
    _term: course._term,
    _courseLength: course._courseLength,
    _courseEffortPerHour: course._courseEffortPerHour,
    _coursePacing: course._coursePacing,
    _coursePrerequisiteLabel: course._coursePrerequisiteLabel,
    _courseDesignations: course._courseDesignations,
    _courseCreditEligible: course._creditEligible
  };
}

// The card gates itself on the date string it prints: MyCourses.vue's isDateInFuture does
// `new Date() < new Date(startDate)`, i.e. local midnight of that Phoenix day. Rebuilt here
// from the parts rather than by re-parsing 'MM/DD/YYYY' — non-ISO string parsing is
// implementation-defined, so the same drop rule could differ between engines.
function startOfDisplayDay(courseDate) {
  const [month, day, year] = courseDate.split('/').map(Number);

  return new Date(year, month - 1, day);
}

// The OD completion window: start + 365, matching MyCourses.vue getFuture365. A queued run
// carries no end date, and the card's own Continuous branch would compute it from today.
function toCompletionWindowEnd(scheduledFor) {
  const [month, day, year] = scheduledFor.split('/').map(Number);
  const end = new Date(year, month - 1, day);
  end.setDate(end.getDate() + 365);

  return `${String(end.getMonth() + 1).padStart(2, '0')}/${String(end.getDate()).padStart(2, '0')}/${end.getFullYear()}`;
}

// U+2011 non-breaking hyphen: the title wraps, and a plain '-' lets it split as
// "(On-" / "demand)". Renders identically.
export const ON_DEMAND_SUFFIX = '(On‑demand)';

// Says which upcoming cards are the on-demand preregistrations. Skipped when the name
// already says it, however spelled, so a rename cannot double the suffix.
export function withOnDemandSuffix(courseName) {
  if (!courseName || /on[-‑ ]?demand/i.test(courseName)) return courseName;

  return `${courseName} ${ON_DEMAND_SUFFIX}`;
}

/**
 * Projects one queued run onto an enrollment-shaped object, or null if it can't be shown.
 *
 * @param run api-hub's projection: { run_id, status, scheduled_for, offering }
 * @param catalogByOfferingId CourseModel by Salesforce courseOfferingId (see indexCatalog)
 * @param now injectable for tests
 */
export function projectPreregisteredRun(run, catalogByOfferingId = new Map(), now = new Date()) {
  const offering = run?.offering || {};
  const scheduledAt = run?.scheduled_for ? new Date(run.scheduled_for) : null;
  const scheduledFor = toCourseDate(run?.scheduled_for);

  // No date means nothing to gate the card on: it would render an openable "Access
  // Course" for a course the learner is not enrolled in yet.
  if (!run?.run_id || !scheduledFor) return null;

  // A queued run whose date has passed is the scheduler running late, not an upcoming
  // course — showing it would ungate the CTA against an enrollment that doesn't exist.
  // It leaves this list the moment the run fires and becomes a real enrollment.
  //
  // Two comparisons, because the run is scheduled at an *instant* while the card is gated
  // on a *date*: the run has already fired, or the card would no longer grey it out.
  if (scheduledAt.getTime() <= now.getTime()) return null;
  if (startOfDisplayDay(scheduledFor).getTime() <= now.getTime()) return null;

  const courseOfferingId = offering.course_offering_id || null;
  // The catalog entry api-hub carried on this read first: a catalog the connector loaded
  // earlier (Add Courses) outlives navigation and view-as switches, so it can be stale.
  const catalogCourse =
    (offering.catalog_course ? new CourseModel(offering.catalog_course) : null) ||
    (courseOfferingId ? catalogByOfferingId.get(String(courseOfferingId)) : null);
  const courseName = catalogCourse?._courseName || offering.name || '';

  // api-hub returns `offering: null` for a run whose offering it could not resolve, and the
  // catalog may not carry it either. With no name from either source there is nothing to
  // label the card with, and the card's `title` is required — so drop it rather than render
  // a blank row where a course should be.
  if (!courseName) return null;

  return {
    ...READ_ONLY_ENROLLMENT_FIELDS,
    // Not an enrollment id — nothing may look this up in coursesStore.enrollments. The
    // prefix keeps it from colliding with one in the views' :key and in any find().
    _enrollmentId: `prereg-${run.run_id}`,
    _isPreregistered: true,
    _preregistrationRunId: run.run_id,
    _courseOfferingId: courseOfferingId,
    _courseInfo: {
      ...courseInfoFromCatalog(catalogCourse),
      // Falls back to the offering's own name and prefix (ULC's course where ULC knows it)
      // when the course is not in this learner's catalog (partner catalogs drift): a named
      // card beats a blank one.
      _courseName: withOnDemandSuffix(courseName),
      _coursePrefix: catalogCourse?._coursePrefix || offering.course_prefix || '',
      _coursePrefixDisplayLabel: catalogCourse?._coursePrefixDisplayLabel || '',
      _courseOfferingId: courseOfferingId,
      // Both, because the card picks one by course type: courseStartingInFuture reads
      // _courseRegistrationDate for a Continuous course and _courseStartDate otherwise.
      _courseStartDate: scheduledFor,
      _courseRegistrationDate: scheduledFor,
      // _courseType stays '': 'Continuous' makes the sort overwrite the start date with today,
      // and makes the card end the course a year from today rather than from the scheduled day.
      _courseEndDate: toCompletionWindowEnd(scheduledFor),
      _courseType: '',
      _enrollmentStatus: 'Active'
    }
  };
}

/** CourseModel[] -> Map keyed by Salesforce courseOfferingId, for the offering join. */
export function indexCatalog(courses = []) {
  return new Map(
    (courses || [])
      .filter((course) => course?._courseOfferingId)
      .map((course) => [String(course._courseOfferingId), course])
  );
}

/** api-hub's `scheduled` array -> renderable upcoming courses. */
export function projectPreregisteredRuns(runs, { catalog = [], now = new Date() } = {}) {
  const catalogByOfferingId = indexCatalog(catalog);

  return (runs || []).map((run) => projectPreregisteredRun(run, catalogByOfferingId, now)).filter(Boolean);
}

/**
 * Adds the preregistered courses to a built "My Upcoming Courses" list.
 *
 * Deduped against the live enrollments rather than at projection time, because that is
 * the moment both sources are current: between a run firing and Aether dropping it from
 * the queued read, the same course exists in both, and the enrollment is the real one.
 *
 * The caller sorts — the two sources interleave chronologically.
 */
export function mergePreregisteredUpcoming(upcomingCourses = [], preregistered = [], enrollments = []) {
  if (!preregistered?.length) return upcomingCourses || [];

  const enrolledOfferingIds = new Set(
    (enrollments || []).map((enrollment) => String(enrollment?._courseOfferingId || '')).filter(Boolean)
  );

  return (upcomingCourses || []).concat(
    preregistered.filter((course) => !enrolledOfferingIds.has(String(course?._courseOfferingId || '')))
  );
}
